import React, { useState } from 'react'
import { Box, Card, CardActionArea, Chip, CircularProgress, Grid, Popover, Typography } from '@mui/material'
import { Dashboard, DashboardConfig } from '../../../types/gotypes'
import { SYSTEM_TEMPLATES, SystemTemplate, allTemplateTags } from './systemTemplates'
import TemplateThumbnail from './TemplateThumbnail'

/** What the caller gets back when a card is picked — either a hardcoded
 * system template (built fresh for `job`) or a stored personal/company
 * template row, distinguished so DashboardCreatePage can route each to the
 * right query param on the way to the layout builder. */
export type TemplateChoice =
  | { kind: 'system'; template: SystemTemplate }
  | { kind: 'stored'; dashboard: Dashboard }

interface DisplayTemplate {
  key: string
  name: string
  description: string
  tags: string[]
  config: DashboardConfig
  choice: TemplateChoice
}

interface TemplateLibraryProps {
  job: string
  /** Shown in the "[Customer] Templates" section heading — falls back to a generic label when omitted. */
  customerDisplayName?: string
  /** Promoted templates visible to everyone in the customer (#971 Phase 2). Fetched by the page via useDashboardTemplates. */
  companyTemplates?: Dashboard[]
  /** The caller's own, unpromoted templates (#971 Phase 2). */
  personalTemplates?: Dashboard[]
  templatesLoading?: boolean
  onSelect: (choice: TemplateChoice) => void
}

/**
 * The "Start from a template" path. Three sections, same tag/preview
 * treatment throughout — Phase 1 shipped System only; Phase 2 adds Company
 * (promoted) and Personal (the caller's own) above it, both backed by real
 * dashboards captured via "Save as template" and rendered from their stored
 * config the same way TemplateThumbnail already renders a system template's
 * `build(job)` output. Filtering by tag applies to System only — its tags are
 * the issue's curated taxonomy; Company/Personal tags are freeform per-
 * template labels shown as informational chips instead.
 */
const TemplateLibrary: React.FC<TemplateLibraryProps> = ({
  job,
  customerDisplayName,
  companyTemplates = [],
  personalTemplates = [],
  templatesLoading = false,
  onSelect,
}) => {
  const [activeTag, setActiveTag] = useState<string | null>(null)
  const [hoverAnchor, setHoverAnchor] = useState<{ el: HTMLElement; template: DisplayTemplate } | null>(null)

  const tags = allTemplateTags()
  const visibleSystem = activeTag ? SYSTEM_TEMPLATES.filter(t => t.tags.includes(activeTag)) : SYSTEM_TEMPLATES

  const systemDisplay: DisplayTemplate[] = visibleSystem.map(t => ({
    key: `system:${t.id}`,
    name: t.name,
    description: t.description,
    tags: t.tags,
    config: t.build(job),
    choice: { kind: 'system', template: t },
  }))

  const storedToDisplay = (dashboard: Dashboard): DisplayTemplate => ({
    key: `stored:${dashboard.id}`,
    name: dashboard.config?.name || 'Untitled template',
    description: dashboard.config?.description || '',
    tags: dashboard.config?.template_tags || [],
    config: dashboard.config || { name: '', description: '', layout: { rows: [] } },
    choice: { kind: 'stored', dashboard },
  })

  const companyDisplay = companyTemplates.map(storedToDisplay)
  const personalDisplay = personalTemplates.map(storedToDisplay)

  const renderGrid = (items: DisplayTemplate[]) => (
    <Grid container spacing={2}>
      {items.map(item => (
        <Grid item xs={12} sm={6} md={4} key={item.key}>
          <Card variant="outlined">
            <CardActionArea
              onClick={() => onSelect(item.choice)}
              onMouseEnter={(e) => setHoverAnchor({ el: e.currentTarget, template: item })}
              onMouseLeave={() => setHoverAnchor(null)}
              sx={{ p: 1.5 }}
            >
              <TemplateThumbnail config={item.config} height={90} />
              <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600 }}>{item.name}</Typography>
              {item.description && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {item.description}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {item.tags.map(tag => (
                  <Chip key={tag} label={tag} size="small" sx={{ height: 18, fontSize: 10 }} />
                ))}
              </Box>
            </CardActionArea>
          </Card>
        </Grid>
      ))}
    </Grid>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {templatesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <>
          {companyDisplay.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                {customerDisplayName ? `${customerDisplayName} Templates` : 'Company Templates'}
              </Typography>
              {renderGrid(companyDisplay)}
            </Box>
          )}

          {personalDisplay.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>My Templates</Typography>
              {renderGrid(personalDisplay)}
            </Box>
          )}
        </>
      )}

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>System Templates</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip
            label="All"
            size="small"
            color={activeTag === null ? 'info' : 'default'}
            variant={activeTag === null ? 'filled' : 'outlined'}
            onClick={() => setActiveTag(null)}
          />
          {tags.map(tag => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              color={activeTag === tag ? 'info' : 'default'}
              variant={activeTag === tag ? 'filled' : 'outlined'}
              onClick={() => setActiveTag(tag)}
            />
          ))}
        </Box>
        {renderGrid(systemDisplay)}
      </Box>

      <Popover
        open={Boolean(hoverAnchor)}
        anchorEl={hoverAnchor?.el || null}
        onClose={() => setHoverAnchor(null)}
        disableRestoreFocus
        sx={{ pointerEvents: 'none' }}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      >
        {hoverAnchor && (
          <Box sx={{ p: 1.5, width: 340 }}>
            <TemplateThumbnail config={hoverAnchor.template.config} height={220} />
            <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600 }}>{hoverAnchor.template.name}</Typography>
            <Typography variant="caption" color="text.secondary">{hoverAnchor.template.description}</Typography>
          </Box>
        )}
      </Popover>
    </Box>
  )
}

export default TemplateLibrary
